import type { AssignmentApp } from "@/lib/data/requests/matters/matter-task";
import { useAppMentionOptions } from "@/hooks/use-app-mention-options";
import type { DirectoryUser } from "@/lib/types";
import {
  convertBackendMessageToDisplay,
  getMentionSegments,
  mentionHighlightChipClassName,
} from "@/lib/utils/mentionUtils";
import { find, type Opts } from "linkifyjs";
import { Fragment, useMemo, type ReactNode } from "react";

const linkifyOptions: Opts = {
  defaultProtocol: "https",
};
const linkClassName = "text-primary underline break-words";

const sanitizeForLinkify = (value: string) => {
  const sanitizedCharacters: string[] = [];
  const indexMap: number[] = [];

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (character === "\n" || character === "\r") {
      continue;
    }
    sanitizedCharacters.push(character);
    indexMap.push(index);
  }

  return {
    sanitizedValue: sanitizedCharacters.join(""),
    indexMap,
  };
};

type LinkifiedSegment =
  | {
      type: "text";
      value: string;
    }
  | {
      type: "link";
      value: string;
      href: string;
      linkType: string;
    };

const buildLinkifiedSegments = (value: string): LinkifiedSegment[] => {
  if (!value) {
    return [{ type: "text", value }];
  }

  const { sanitizedValue, indexMap } = sanitizeForLinkify(value);
  if (!sanitizedValue) {
    return [{ type: "text", value }];
  }

  const matches = find(sanitizedValue, linkifyOptions);
  if (matches.length === 0) {
    return [{ type: "text", value }];
  }

  const segments: LinkifiedSegment[] = [];
  let lastIndex = 0;

  matches.forEach((match) => {
    const startIndex = indexMap[match.start] ?? match.start;
    const lastSanitizedIndex = match.end - 1;
    const endIndex =
      lastSanitizedIndex >= 0 && lastSanitizedIndex < indexMap.length
        ? indexMap[lastSanitizedIndex] + 1
        : value.length;

    if (lastIndex < startIndex) {
      segments.push({
        type: "text",
        value: value.slice(lastIndex, startIndex),
      });
    }

    segments.push({
      type: "link",
      href: match.href,
      linkType: match.type,
      value: value.slice(startIndex, endIndex),
    });
    lastIndex = endIndex;
  });

  if (lastIndex < value.length) {
    segments.push({ type: "text", value: value.slice(lastIndex) });
  }

  return segments;
};

interface TextWithMentionsProps {
  text: string;
  users: DirectoryUser[];
  className?: string;
  mentionedApps?: AssignmentApp[];
}

export const TextWithMentions = ({
  text,
  users,
  className,
  mentionedApps,
}: TextWithMentionsProps) => {
  const mentionOptions = useAppMentionOptions(
    users,
    text.includes("{{app:"),
    mentionedApps,
  );

  const displayMessage = useMemo(
    () => convertBackendMessageToDisplay(text, mentionOptions),
    [text, mentionOptions],
  );

  const mentionSegments = useMemo(
    () => getMentionSegments(displayMessage, mentionOptions),
    [displayMessage, mentionOptions],
  );

  const linkifiedSegments = useMemo(
    () => buildLinkifiedSegments(displayMessage),
    [displayMessage],
  );

  const highlightedMessage = useMemo(() => {
    if (!displayMessage) return "";
    const hasMentions = mentionSegments.some((segment) => segment.option);
    const nodes: ReactNode[] = [];

    linkifiedSegments.forEach((segment, segmentIndex) => {
      if (segment.type === "link") {
        nodes.push(
          <a
            key={`text-with-mentions-link-${segmentIndex}`}
            href={segment.href}
            className={linkClassName}
            target={segment.linkType === "url" ? "_blank" : undefined}
            rel="noreferrer noopener"
          >
            {segment.value}
          </a>,
        );
        return;
      }

      if (!hasMentions) {
        nodes.push(segment.value);
        return;
      }

      const textSegments = getMentionSegments(segment.value, mentionOptions);
      if (textSegments.length === 0) {
        nodes.push(segment.value);
        return;
      }

      textSegments.forEach((textSegment, textIndex) => {
        if (!textSegment.option) {
          nodes.push(
            <Fragment key={`segment-${segmentIndex}-${textIndex}`}>
              {textSegment.value}
            </Fragment>,
          );
          return;
        }
        nodes.push(
          <span
            key={`segment-${segmentIndex}-${textIndex}-${textSegment.option.id}`}
            className={mentionHighlightChipClassName}
          >
            {textSegment.value}
          </span>,
        );
      });
    });

    return nodes;
  }, [displayMessage, linkifiedSegments, mentionOptions, mentionSegments]);

  return <span className={className}>{highlightedMessage}</span>;
};
